import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import {
  Compass, ArrowRight, Search, BookOpen, MapPin, Landmark, Sun,
  Mountain, Heart, TreePine, Star, Calendar, Users as UsersIcon,
  Plane, Map, GripVertical, Clock, ChevronRight, Share2,
  Download, Check, Sparkles, Globe,
} from 'lucide-react';

const SCREENS = [
  {
    id: 'browse',
    title: 'Browse Atlas Files',
    description: 'Explore curated travel guides and itineraries from travelers worldwide. Find inspiration for your next adventure.',
  },
  {
    id: 'designer',
    title: 'Design Your Trip',
    description: 'Tell us where, when, and how you like to travel. Our AI builds a personalized itinerary in seconds.',
  },
  {
    id: 'customize',
    title: 'Customize Your Itinerary',
    description: 'Drag, drop, and fine-tune every detail. Add activities, reorder days, and make the trip truly yours.',
  },
  {
    id: 'share',
    title: 'Save & Share',
    description: 'Export your itinerary, share it with travel companions, or publish it as an Atlas File for others.',
  },
];

// ── Screen 1: Browse Atlas Files ──
function BrowseScreen() {
  const files = [
    { title: '10 Days in Japan',        dest: 'Tokyo · Kyoto · Osaka',  days: 10, img: 'from-rose-300 to-violet-400',   rating: 4.9 },
    { title: 'Italian Coast Road Trip', dest: 'Rome · Amalfi · Sicily', days: 7,  img: 'from-amber-300 to-orange-400',  rating: 4.8 },
    { title: 'Bali Adventure',          dest: 'Ubud · Seminyak · Nusa', days: 8,  img: 'from-emerald-300 to-teal-400',  rating: 4.7 },
  ];

  return (
    <div className="h-full flex flex-col" style={{ background: '#151e2d' }}>
      {/* Subtle glow accent */}
      <div className="absolute top-[-20px] right-[-20px] w-28 h-28 rounded-full bg-coral-400/8 blur-2xl pointer-events-none" />

      <div className="pt-14 px-5 pb-3">
        <h2 className="text-[18px] font-bold text-white mb-3 tracking-tight">Atlas Files</h2>
        <div className="flex items-center gap-2.5 rounded-2xl px-4 py-3" style={{ background: 'rgba(255,255,255,0.07)' }}>
          <Search size={13} className="text-white/35 flex-shrink-0" />
          <span className="text-[11px] text-white/30">Search destinations...</span>
        </div>
      </div>

      <div className="flex-1 px-5 space-y-3 overflow-hidden">
        {files.map((f, i) => (
          <div key={i} className="flex gap-3.5 rounded-2xl p-3" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className={`w-[58px] h-[58px] rounded-xl bg-gradient-to-br ${f.img} flex-shrink-0 flex items-center justify-center shadow-lg`}>
              <Globe size={20} className="text-white/75" />
            </div>
            <div className="flex-1 min-w-0 py-0.5">
              <p className="text-[12px] font-bold text-white leading-tight mb-1 truncate">{f.title}</p>
              <p className="text-[10px] flex items-center gap-0.5 mb-2" style={{ color: 'rgba(255,255,255,0.45)' }}>
                <MapPin size={8} className="flex-shrink-0" /> {f.dest}
              </p>
              <div className="flex items-center gap-3">
                <span className="text-[9px] flex items-center gap-1" style={{ color: 'rgba(255,255,255,0.35)' }}>
                  <Calendar size={9} /> {f.days} days
                </span>
                <span className="text-[9px] flex items-center gap-0.5 text-naples-400 font-medium">
                  <Star size={9} fill="currentColor" /> {f.rating}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="px-5 pb-7 pt-4">
        <button className="w-full py-3 rounded-2xl bg-coral-400 text-white text-[12px] font-bold flex items-center justify-center gap-2 shadow-lg" style={{ boxShadow: '0 4px 20px rgba(239,133,87,0.35)' }}>
          <BookOpen size={13} /> Browse All Files
        </button>
      </div>
    </div>
  );
}

// ── Screen 2: Travel Designer ──
function DesignerScreen() {
  return (
    <div className="h-full flex flex-col bg-[#fafaf8] px-5 pt-14">
      <div className="mb-4">
        <h2 className="text-[17px] font-bold text-charcoal-500 mb-0.5">Plan Your Trip</h2>
        <p className="text-[11px] text-platinum-500">Tell us about your dream journey</p>
      </div>

      <div className="space-y-3 mb-4">
        <div>
          <label className="text-[10px] font-medium text-charcoal-400 mb-1 block">Destination</label>
          <div className="flex items-center gap-2 bg-white rounded-xl border border-platinum-200 px-3.5 py-2.5">
            <MapPin size={13} className="text-coral-400" />
            <span className="text-[12px] text-charcoal-500">Lisbon, Portugal</span>
          </div>
        </div>
        <div className="flex gap-2.5">
          <div className="flex-1">
            <label className="text-[10px] font-medium text-charcoal-400 mb-1 block">Duration</label>
            <div className="flex items-center gap-2 bg-white rounded-xl border border-platinum-200 px-3.5 py-2.5">
              <Calendar size={13} className="text-columbia-500" />
              <span className="text-[12px] text-charcoal-500">7 days</span>
            </div>
          </div>
          <div className="flex-1">
            <label className="text-[10px] font-medium text-charcoal-400 mb-1 block">Travelers</label>
            <div className="flex items-center gap-2 bg-white rounded-xl border border-platinum-200 px-3.5 py-2.5">
              <UsersIcon size={13} className="text-columbia-500" />
              <span className="text-[12px] text-charcoal-500">2 people</span>
            </div>
          </div>
        </div>
        <div>
          <label className="text-[10px] font-medium text-charcoal-400 mb-1.5 block">Travel Style</label>
          <div className="flex flex-wrap gap-1.5">
            {[
              { label: 'Culture', Icon: Landmark, active: true },
              { label: 'Food', Icon: Sun, active: true },
              { label: 'Nature', Icon: TreePine, active: false },
              { label: 'Adventure', Icon: Mountain, active: false },
              { label: 'Relaxation', Icon: Heart, active: false },
            ].map((s, i) => (
              <span key={i} className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-medium ${
                s.active ? 'bg-coral-50 text-coral-600 border border-coral-200' : 'bg-white text-platinum-500 border border-platinum-200'
              }`}>
                <s.Icon size={10} /> {s.label}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-auto pb-8">
        <button className="w-full py-2.5 rounded-xl bg-charcoal-500 text-white text-[13px] font-semibold flex items-center justify-center gap-1.5">
          <Sparkles size={14} /> Generate Itinerary
        </button>
      </div>
    </div>
  );
}

// ── Screen 3: Customize Itinerary ──
function CustomizeScreen() {
  const activities = [
    { time: '9:00',  name: 'Belem Tower',        color: '#8B5CF6', Icon: Landmark },
    { time: '11:30', name: 'Pasteis de Belem',    color: '#F59E0B', Icon: Sun },
    { time: '14:00', name: 'Alfama District',      color: '#8B5CF6', Icon: Landmark },
    { time: '17:00', name: 'Sunset at Miradouro', color: '#10B981', Icon: TreePine },
  ];

  return (
    <div className="h-full flex flex-col bg-white px-5 pt-14">
      <div className="mb-3">
        <h2 className="text-[17px] font-bold text-charcoal-500 tracking-tight">Lisbon, 7 Days</h2>
        <p className="text-[10px] text-platinum-400 mt-0.5">Drag to reorder, tap to edit</p>
      </div>

      {/* Day header */}
      <div className="rounded-xl px-4 py-2.5 mb-3 flex items-center justify-between" style={{ background: '#2C4251' }}>
        <span className="text-[12px] font-bold text-white">Day 1</span>
        <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.45)' }}>Belem &amp; Alfama</span>
      </div>

      {/* Activities */}
      <div className="space-y-2 mb-3">
        {activities.map((a, i) => (
          <div key={i} className="flex items-center gap-2.5 bg-white rounded-xl px-3 py-2.5 border border-platinum-100" style={{ boxShadow: '0 1px 4px rgba(44,66,81,0.06)' }}>
            <GripVertical size={11} className="text-platinum-300 flex-shrink-0" />
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${a.color}18` }}>
              <a.Icon size={13} style={{ color: a.color }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-semibold text-charcoal-500 truncate leading-tight">{a.name}</p>
              <p className="text-[9px] text-platinum-400 flex items-center gap-0.5 mt-0.5">
                <Clock size={8} /> {a.time}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Map tile */}
      <div className="rounded-xl flex items-center justify-center gap-2 mb-3" style={{ height: '62px', background: 'rgba(157,192,207,0.18)', border: '1px solid rgba(157,192,207,0.35)' }}>
        <Map size={16} className="text-columbia-500" />
        <span className="text-[10px] text-columbia-600 font-medium">Interactive Map</span>
      </div>

      <div className="mt-auto pb-7">
        <button className="w-full py-3 rounded-2xl bg-coral-400 text-white text-[12px] font-bold flex items-center justify-center gap-1.5" style={{ boxShadow: '0 4px 16px rgba(239,133,87,0.30)' }}>
          <Check size={13} /> Save Changes
        </button>
      </div>
    </div>
  );
}

// ── Screen 4: Save & Share ──
function ShareScreen() {
  return (
    <div className="h-full flex flex-col bg-[#fafaf8] relative overflow-hidden">
      <div className="absolute top-[-20px] left-1/2 -translate-x-1/2 w-40 h-40 rounded-full bg-coral-400/8 blur-3xl" />

      <div className="relative flex-1 flex flex-col items-center justify-center px-6 text-center">
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-coral-400 to-coral-500 flex items-center justify-center mb-5 shadow-lg shadow-coral-400/20">
          <Check size={28} className="text-white" strokeWidth={2.5} />
        </div>

        <h2 className="text-[18px] font-bold text-charcoal-500 mb-1.5">Trip Ready!</h2>
        <p className="text-[11px] text-platinum-500 mb-6">Your Lisbon itinerary is saved. Share it with your travel buddies.</p>

        <div className="w-full space-y-2.5 mb-4">
          <div className="flex items-center gap-3 bg-white rounded-xl p-3 border border-platinum-100 shadow-sm">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-coral-100 to-naples-100 flex items-center justify-center">
              <Share2 size={16} className="text-coral-500" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-[12px] font-semibold text-charcoal-500">Share with Friends</p>
              <p className="text-[10px] text-platinum-500">Send a link to your companions</p>
            </div>
            <ChevronRight size={14} className="text-platinum-400" />
          </div>

          <div className="flex items-center gap-3 bg-white rounded-xl p-3 border border-platinum-100 shadow-sm">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-columbia-100 to-columbia-200 flex items-center justify-center">
              <Download size={16} className="text-columbia-600" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-[12px] font-semibold text-charcoal-500">Export as PDF</p>
              <p className="text-[10px] text-platinum-500">Offline access to your itinerary</p>
            </div>
            <ChevronRight size={14} className="text-platinum-400" />
          </div>

          <div className="flex items-center gap-3 bg-white rounded-xl p-3 border border-platinum-100 shadow-sm">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-naples-100 to-naples-200 flex items-center justify-center">
              <Globe size={16} className="text-naples-600" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-[12px] font-semibold text-charcoal-500">Publish as Atlas File</p>
              <p className="text-[10px] text-platinum-500">Help other travelers discover Lisbon</p>
            </div>
            <ChevronRight size={14} className="text-platinum-400" />
          </div>
        </div>
      </div>

      <div className="relative px-6 pb-8">
        <button className="w-full py-2.5 rounded-xl bg-coral-400 text-white text-[13px] font-semibold flex items-center justify-center gap-1.5 shadow-lg shadow-coral-400/20">
          <Plane size={14} /> View My Trip
        </button>
      </div>
    </div>
  );
}

const SCREEN_COMPONENTS = [BrowseScreen, DesignerScreen, CustomizeScreen, ShareScreen];

// Mobile: the desktop version below pins the phone with a 350vh scroll-jacked
// section (position: sticky + per-screen opacity driven by scroll progress).
// That combination reliably fails to paint on mobile — the computed styles
// are correct (verified: opacity/transform resolve as expected) but nothing
// renders, a known sticky+overflow+nested-transform compositing failure.
// Scroll-jacking is also a poor mobile pattern on its own (650px+ of static
// scroll distance per screen). Mobile gets a plain stacked list instead, no
// sticky, no scroll-linked motion.
function PhoneShowcaseMobile() {
  return (
    <section className="lg:hidden py-16 px-6 bg-[#FAF7F2]">
      <div className="space-y-14 max-w-sm mx-auto">
        {SCREENS.map((screen, i) => {
          const ScreenComp = SCREEN_COMPONENTS[i];
          return (
            <div key={screen.id} className="flex flex-col items-center text-center">
              <span className="inline-flex items-center gap-1.5 text-coral-400 text-xs font-medium tracking-wider uppercase mb-3">
                <span className="w-5 h-5 rounded-md bg-coral-50 flex items-center justify-center text-[10px] font-bold">{i + 1}</span>
                of {SCREENS.length}
              </span>
              <h3 className="text-2xl font-semibold text-charcoal-500 mb-3">{screen.title}</h3>
              <p className="text-charcoal-400 leading-relaxed mb-7">{screen.description}</p>

              <div className="relative flex-shrink-0">
                <div className="relative w-[300px] h-[610px] rounded-[3rem] bg-[#1a1a1a] p-[8px] shadow-[0_20px_80px_rgba(0,0,0,0.2),0_0_0_1px_rgba(255,255,255,0.05)_inset]">
                  <div className="absolute top-4 left-1/2 -translate-x-1/2 w-24 h-6 bg-black rounded-full z-20" />
                  <div className="w-full h-full rounded-[2.7rem] overflow-hidden bg-[#fafaf8] relative">
                    <div className="absolute top-0 left-0 right-0 h-10 z-10 flex items-end justify-between px-6 pb-0.5">
                      <span className="text-[8px] font-semibold text-white/70">9:41</span>
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-1.5 rounded-sm border border-white/50">
                          <div className="w-2 h-full bg-white/50 rounded-sm" />
                        </div>
                      </div>
                    </div>
                    <ScreenComp />
                  </div>
                </div>
                <div className="absolute -inset-16 -z-10 rounded-full bg-gradient-to-br from-coral-200/30 via-naples-200/20 to-columbia-200/25 blur-3xl" />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function PhoneShowcaseDesktop() {
  const containerRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end'],
  });

  // Phone entrance — start already visible so the phone rises in "almost
  // ready" as the globe scrolls away (no blank dead-zone at the seam).
  const phoneScale = useTransform(scrollYProgress, [0, 0.08, 0.85, 1], [0.94, 1, 1, 0.6]);
  const phoneOpacity = useTransform(scrollYProgress, [0, 0.88, 1], [1, 1, 0]);

  // Screen opacities — first screen is visible immediately, rest get ~25% each
  const screen0Opacity = useTransform(scrollYProgress, [0, 0.2, 0.25], [1, 1, 0]);
  const screen1Opacity = useTransform(scrollYProgress, [0.22, 0.28, 0.45, 0.5], [0, 1, 1, 0]);
  const screen2Opacity = useTransform(scrollYProgress, [0.47, 0.53, 0.7, 0.75], [0, 1, 1, 0]);
  const screen3Opacity = useTransform(scrollYProgress, [0.72, 0.78, 0.92, 1], [0, 1, 1, 0]);
  const screenOpacities = [screen0Opacity, screen1Opacity, screen2Opacity, screen3Opacity];

  // Feature text — first block visible immediately alongside the phone
  const text0Opacity = useTransform(scrollYProgress, [0, 0.2, 0.24], [1, 1, 0]);
  const text1Opacity = useTransform(scrollYProgress, [0.24, 0.3, 0.45, 0.49], [0, 1, 1, 0]);
  const text2Opacity = useTransform(scrollYProgress, [0.49, 0.55, 0.7, 0.74], [0, 1, 1, 0]);
  const text3Opacity = useTransform(scrollYProgress, [0.74, 0.8, 0.92, 0.96], [0, 1, 1, 0]);
  const textOpacities = [text0Opacity, text1Opacity, text2Opacity, text3Opacity];

  const text0Y = useTransform(scrollYProgress, [0, 0.2, 0.24], [0, 0, -30]);
  const text1Y = useTransform(scrollYProgress, [0.24, 0.3, 0.45, 0.49], [30, 0, 0, -30]);
  const text2Y = useTransform(scrollYProgress, [0.49, 0.55, 0.7, 0.74], [30, 0, 0, -30]);
  const text3Y = useTransform(scrollYProgress, [0.74, 0.8, 0.92, 0.96], [30, 0, 0, -30]);
  const textYs = [text0Y, text1Y, text2Y, text3Y];

  const activeDot = useTransform(scrollYProgress, [0, 0.25, 0.5, 0.75, 1], [0, 1, 2, 3, 3]);

  return (
    <section ref={containerRef} className="relative hidden lg:block" style={{ height: '350vh' }}>
      <div className="sticky top-0 h-screen flex items-center justify-center overflow-hidden bg-[#FAF7F2]">
        <div className="container mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center justify-center gap-12 lg:gap-20">

            {/* Feature text */}
            <div className="relative w-full lg:w-96 h-32 lg:h-48">
              {SCREENS.map((screen, i) => (
                <motion.div
                  key={screen.id}
                  className="absolute inset-0 flex flex-col justify-center text-center lg:text-left"
                  style={{ opacity: textOpacities[i], y: textYs[i] }}
                >
                  <span className="inline-flex items-center gap-1.5 text-coral-400 text-xs font-medium tracking-wider uppercase mb-3">
                    <span className="w-5 h-5 rounded-md bg-coral-50 flex items-center justify-center text-[10px] font-bold">{i + 1}</span>
                    of {SCREENS.length}
                  </span>
                  <h3 className="text-2xl lg:text-3xl font-semibold text-charcoal-500 mb-3">
                    {screen.title}
                  </h3>
                  <p className="text-charcoal-400 leading-relaxed">
                    {screen.description}
                  </p>
                </motion.div>
              ))}
            </div>

            {/* Phone */}
            <motion.div
              className="relative flex-shrink-0"
              style={{ scale: phoneScale, opacity: phoneOpacity }}
            >
              {/* Phone frame */}
              <div className="relative w-[300px] h-[610px] rounded-[3rem] bg-[#1a1a1a] p-[8px] shadow-[0_20px_80px_rgba(0,0,0,0.2),0_0_0_1px_rgba(255,255,255,0.05)_inset]">
                {/* Dynamic Island */}
                <div className="absolute top-4 left-1/2 -translate-x-1/2 w-24 h-6 bg-black rounded-full z-20" />

                {/* Screen */}
                <div className="w-full h-full rounded-[2.7rem] overflow-hidden bg-[#fafaf8] relative">
                  {/* Status bar */}
                  <div className="absolute top-0 left-0 right-0 h-10 z-10 flex items-end justify-between px-6 pb-0.5">
                    <span className="text-[8px] font-semibold text-white/70">9:41</span>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-1.5 rounded-sm border border-white/50">
                        <div className="w-2 h-full bg-white/50 rounded-sm" />
                      </div>
                    </div>
                  </div>

                  {/* Screen content layers */}
                  {SCREEN_COMPONENTS.map((ScreenComp, i) => (
                    <motion.div
                      key={i}
                      className="absolute inset-0"
                      style={{ opacity: screenOpacities[i] }}
                    >
                      <ScreenComp />
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Glow behind phone */}
              <div className="absolute -inset-16 -z-10 rounded-full bg-gradient-to-br from-coral-200/30 via-naples-200/20 to-columbia-200/25 blur-3xl" />
            </motion.div>

            {/* Progress dots */}
            <div className="flex lg:flex-col gap-2.5">
              {SCREENS.map((_, i) => (
                <ProgressDot key={i} index={i} activeDot={activeDot} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ProgressDot({ index, activeDot }) {
  const isActive = useTransform(activeDot, (v) => Math.round(v) === index);
  const scale = useTransform(isActive, (active) => active ? 1.4 : 1);
  const bgOpacity = useTransform(isActive, (active) => active ? 1 : 0.3);

  return (
    <motion.div
      className="w-2.5 h-2.5 rounded-full bg-coral-400"
      style={{ scale, opacity: bgOpacity }}
    />
  );
}

function PhoneShowcase() {
  return (
    <>
      <PhoneShowcaseDesktop />
      <PhoneShowcaseMobile />
    </>
  );
}

export default PhoneShowcase;
